/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IVideoPlayerAdditionalContentViewProps, IVideoPlayerViewProps } from './index';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IVideoPlayerAdditionalContentViewProps) => {
    if (additionalContent.additionalContentItems) {
        return (
            <Node {...additionalContent.additionalContentNode}>
                <>
                    {additionalContent.additionalContentItems.heading}
                    <Node {...additionalContent.additionalContentItems.additionalContentItemContainer}>
                        {additionalContent.additionalContentItems.text}
                        <Node {...additionalContent.additionalContentItems.additionalContentItemLinks}>
                            {additionalContent.additionalContentItems.links}
                        </Node>
                    </Node>
                </>
            </Node>
        );
    }

    return null;
};

const VideoPlayerView: React.FC<IVideoPlayerViewProps> = props => {
    const { config, renderModuleAttributes, id } = props;

    const videoLink = config.videoLink;

    return (
        <div id={id} className='ms-ext-video-player' {...renderModuleAttributes(props)}>
            {!!videoLink ? (
                <a className='ms-ext-video-player__link' href={videoLink?.destinationUrl}>
                    <VideoPlayerComponent {...props} />
                </a>
            ) : (
                <VideoPlayerComponent {...props} />
            )}
        </div>
    );
};

function VideoPlayerComponent({ config, player, text, title, ...props }: IVideoPlayerViewProps) {
    const topMargin = config.topMargin || 0;
    const rightMargin = config.rightMargin || 0;
    const bottomMargin = config.bottomMargin || 0;
    const leftMargin = config.leftMargin || 0;
    const contentPositionY = config.contentPositionY;
    const contentPositionX = config.contentPositionX;
    const textAlignment = config.textAlignment;
    return (
        <div className='ms-ext-video-player__content'>
            {player}
            <div
                style={{
                    display: 'flex',
                    alignItems: contentPositionY,
                    justifyContent: contentPositionX,
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    transform: 'translate(0%, -100%)'
                }}
            >
                <Node {...props.detailsContainer} className={props.detailsContainer ? props.detailsContainer.className : ''}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: textAlignment,
                            justifyContent: 'center',
                            textAlign: textAlignment,
                            marginTop: `${topMargin}px`,
                            marginRight: `${rightMargin}px`,
                            marginBottom: `${bottomMargin}px`,
                            marginLeft: `${leftMargin}px`,
                            width: '100%',
                            height: '100%'
                        }}
                    >
                        {text}
                        {title}
                        {props.additionalContent && renderAdditionalContent(props.additionalContent)}
                        <Node
                            {...props.detailsContainerCTA}
                            className={props.detailsContainerCTA ? props.detailsContainerCTA.className : ''}
                        >
                            {props.links}
                        </Node>
                    </div>
                </Node>
            </div>
        </div>
    );
}

export default VideoPlayerView;
